import { apiClient } from './api';

// Users
interface AdminUsersFilters { page?: number; perPage?: number; search?: string; role?: string; status?: string }
export async function getAdminUsers(filters: AdminUsersFilters) {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.perPage) params.set('perPage', String(filters.perPage));
  if (filters.search) params.set('search', filters.search);
  if (filters.role) params.set('role', filters.role);
  if (filters.status) params.set('status', filters.status);
  const res = await apiClient.get(`/users?${params}`);
  return res.data.data;
}

export async function getAdminUser(userId: string) {
  const res = await apiClient.get(`/users/${userId}`);
  return res.data.data;
}

export async function createAdminUser(data: {
  email: string; password: string; firstName: string; lastName: string; role: string;
  phone?: string; company?: string; notes?: string;
  subscription?: { plan: string; expiresAt?: string; maxSites?: number; priceAmount?: number };
}) {
  const res = await apiClient.post('/users', data);
  return res.data.data;
}

export async function updateAdminUser(userId: string, data: Record<string, unknown>) {
  const res = await apiClient.put(`/users/${userId}`, data);
  return res.data.data;
}

export async function updateAdminUserStatus(userId: string, status: string) {
  const res = await apiClient.put(`/users/${userId}/status`, { status });
  return res.data.data;
}

export async function deleteAdminUser(userId: string) {
  const res = await apiClient.delete(`/users/${userId}`);
  return res.data.data;
}

export async function adminResetPassword(userId: string, newPassword: string) {
  const res = await apiClient.post(`/users/${userId}/reset-password`, { newPassword });
  return res.data.data;
}

// Subscriptions
interface SubsFilters { page?: number; perPage?: number; status?: string; plan?: string; expiringDays?: number }
export async function getAdminSubscriptions(filters: SubsFilters) {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.perPage) params.set('perPage', String(filters.perPage));
  if (filters.status) params.set('status', filters.status);
  if (filters.plan) params.set('plan', filters.plan);
  if (filters.expiringDays) params.set('expiringDays', String(filters.expiringDays));
  const res = await apiClient.get(`/subscriptions?${params}`);
  return res.data.data;
}

export async function extendSubscription(userId: string, days: number) {
  const res = await apiClient.post(`/subscriptions/${userId}/extend`, { days });
  return res.data.data;
}

export async function changeSubscriptionPlan(userId: string, data: { plan: string; maxSites?: number; priceAmount?: number }) {
  const res = await apiClient.put(`/subscriptions/${userId}`, data);
  return res.data.data;
}

// Sites management (admin)
export async function getAdminSites(filters?: { page?: number; perPage?: number; search?: string; status?: string }) {
  const params = new URLSearchParams();
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.perPage) params.set('perPage', String(filters.perPage));
  if (filters?.search) params.set('search', filters.search);
  if (filters?.status) params.set('status', filters.status);
  const res = await apiClient.get(`/sites?${params}`);
  return res.data.data;
}

export async function connectSite(data: { name: string; url: string; wcConsumerKey: string; wcConsumerSecret: string; bridgeApiKey?: string; userId?: string }) {
  const res = await apiClient.post('/sites', data);
  return res.data.data;
}

export async function updateSite(siteId: string, data: { name?: string; url?: string; wcConsumerKey?: string; wcConsumerSecret?: string; bridgeApiKey?: string }) {
  const res = await apiClient.put(`/sites/${siteId}`, data);
  return res.data.data;
}

export async function deleteSite(siteId: string) {
  await apiClient.delete(`/sites/${siteId}`);
}

export async function testSiteConnection(siteId: string) {
  const res = await apiClient.post(`/sites/${siteId}/test`);
  return res.data.data;
}

// Site user assignments & permissions
export async function assignUserToSite(siteId: string, userId: string) {
  const res = await apiClient.post(`/sites/${siteId}/assign`, { userId });
  return res.data.data;
}

export async function unassignUserFromSite(siteId: string, userId: string) {
  await apiClient.delete(`/sites/${siteId}/assign/${userId}`);
}

export async function getSitePermissions(siteId: string) {
  const res = await apiClient.get(`/sites/${siteId}/permissions`);
  return res.data.data;
}

export async function updateSitePermissions(siteId: string, userId: string, permissions: Record<string, boolean>) {
  const res = await apiClient.put(`/sites/${siteId}/permissions/${userId}`, permissions);
  return res.data.data;
}

// Plugins
export async function getAdminPlugins() {
  const res = await apiClient.get('/admin/plugins');
  return res.data.data;
}

export async function createAdminPlugin(data: Record<string, unknown>) {
  const res = await apiClient.post('/admin/plugins', data);
  return res.data.data;
}

export async function updateAdminPlugin(pluginId: string, data: Record<string, unknown>) {
  const res = await apiClient.put(`/admin/plugins/${pluginId}`, data);
  return res.data.data;
}

export async function deleteAdminPlugin(pluginId: string) {
  await apiClient.delete(`/admin/plugins/${pluginId}`);
}

// Bridge plugin info
export interface BridgePluginInfo {
  version: string;
  requiresWP: string;
  requiresPHP: string;
  requiresWC: string;
  testedWC: string;
  changelog: Array<{ version: string; date: string; changes: string[] }>;
}

export async function getBridgePluginInfo(): Promise<BridgePluginInfo> {
  const res = await apiClient.get('/admin/bridge-plugin/info');
  return res.data.data;
}

// Bridge plugin download
export async function downloadBridgePlugin(): Promise<Blob> {
  const res = await apiClient.get('/admin/bridge-plugin/download', {
    responseType: 'blob',
  });
  return new Blob([res.data], { type: 'application/zip' });
}

// AI Usage
export async function getAdminAIUsage() {
  const res = await apiClient.get('/ai/usage/all');
  return res.data.data;
}

// Audit Log
interface AuditFilters { page?: number; perPage?: number; userId?: string; siteId?: string; action?: string; from?: string; to?: string }
export async function getAdminAuditLog(filters: AuditFilters) {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.perPage) params.set('perPage', String(filters.perPage));
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.siteId) params.set('siteId', filters.siteId);
  if (filters.action) params.set('action', filters.action);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  const res = await apiClient.get(`/admin/audit-log?${params}`);
  return res.data.data;
}

// Role Default Permissions
export async function getRoleDefaults() {
  const res = await apiClient.get('/admin/role-defaults');
  return res.data.data;
}

export async function updateRoleDefaults(data: Record<string, Record<string, boolean>>) {
  const res = await apiClient.put('/admin/role-defaults', data);
  return res.data.data;
}

// App Settings
export async function getAdminSettings() {
  const res = await apiClient.get('/admin/settings');
  return res.data.data;
}

export async function updateAdminSetting(key: string, value: string) {
  const res = await apiClient.put(`/admin/settings/${key}`, { value });
  return res.data.data;
}
